import { createContext, useContext, useState, useEffect, useCallback, ReactNode, useMemo } from 'react';
import { FeatureFlags, RoleFeatures, ToggleableRole, FeatureName, DEFAULT_ROLE_FEATURES, UserRole } from '../types';
import featureFlagsService from '../services/feature-flags.service';
import { useAuth } from './AuthContext';

// ============================================================================
// FEATURE FLAGS CONTEXT TYPES
// ============================================================================

interface FeatureFlagsContextType {
  featureFlags: FeatureFlags | null;
  isLoading: boolean;
  error: string | null;
  refetchFlags: () => Promise<void>;
  // Role-based toggles
  toggleAllFeatures: (enabled: boolean) => Promise<void>;
  toggleRoleFeatures: (role: ToggleableRole, enabled: boolean) => Promise<void>;
  toggleFeature: (role: ToggleableRole, feature: FeatureName, enabled: boolean) => Promise<void>;
  // Helper functions to check if features are enabled for current user's role
  isFeatureEnabled: (feature: FeatureName) => boolean;
  getFeaturesForRole: (role: ToggleableRole) => RoleFeatures;
  // Computed values for current user
  currentUserFeatures: RoleFeatures;
}

// Default role features - all disabled
const defaultRoleFeatures: RoleFeatures = { ...DEFAULT_ROLE_FEATURES };

// Default feature flags state
const defaultFlags: FeatureFlags = {
  featureFlagsId: 'global',
  admin: { ...defaultRoleFeatures },
  hr: { ...defaultRoleFeatures },
  employee: { ...defaultRoleFeatures },
  updatedAt: '',
  updatedBy: '',
};

// ============================================================================
// FEATURE FLAGS CONTEXT
// ============================================================================

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

// ============================================================================
// FEATURE FLAGS PROVIDER
// ============================================================================

interface FeatureFlagsProviderProps {
  children: ReactNode;
}

export function FeatureFlagsProvider({ children }: FeatureFlagsProviderProps) {
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, isSuperAdmin, user } = useAuth();

  // Fetch feature flags
  const fetchFlags = useCallback(async () => {
    if (!isAuthenticated) {
      setFeatureFlags(defaultFlags);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const flags = await featureFlagsService.getFeatureFlags();
      setFeatureFlags(flags);
    } catch (err) {
      console.error('Error fetching feature flags:', err);
      setError('Failed to load feature flags');
      setFeatureFlags(defaultFlags);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Initial fetch
  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  // Refetch flags
  const refetchFlags = useCallback(async () => {
    await fetchFlags();
  }, [fetchFlags]);

  // Toggle all features for all roles (Super Admin only)
  const toggleAllFeatures = useCallback(async (enabled: boolean) => {
    if (!isSuperAdmin) {
      setError('Only Super Admin can toggle features');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const updatedFlags = await featureFlagsService.toggleAllFeatures(enabled);
      setFeatureFlags(updatedFlags);
    } catch (err: any) {
      console.error('Error toggling all features:', err);
      const errorMessage = err?.message || err?.code || 'Failed to toggle features';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [isSuperAdmin]);

  // Toggle all features for a specific role (Super Admin only)
  const toggleRoleFeatures = useCallback(async (role: ToggleableRole, enabled: boolean) => {
    if (!isSuperAdmin) {
      setError('Only Super Admin can toggle features');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const updatedFlags = await featureFlagsService.toggleRoleFeatures(role, enabled);
      setFeatureFlags(updatedFlags);
    } catch (err: any) {
      console.error('Error toggling role features:', err);
      const errorMessage = err?.message || err?.code || 'Failed to toggle role features';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [isSuperAdmin]);

  // Toggle a specific feature for a specific role (Super Admin only)
  const toggleFeature = useCallback(async (role: ToggleableRole, feature: FeatureName, enabled: boolean) => {
    if (!isSuperAdmin) {
      setError('Only Super Admin can toggle features');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const updatedFlags = await featureFlagsService.toggleFeature(role, feature, enabled);
      setFeatureFlags(updatedFlags);
    } catch (err: any) {
      console.error('Error toggling feature:', err);
      const errorMessage = err?.message || err?.code || 'Failed to toggle feature';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [isSuperAdmin]);

  // Get features for a specific role
  const getFeaturesForRole = useCallback((role: ToggleableRole): RoleFeatures => {
    if (!featureFlags) return defaultRoleFeatures;
    return featureFlags[role] || defaultRoleFeatures;
  }, [featureFlags]);

  // Get features for current user based on their role
  const currentUserFeatures = useMemo((): RoleFeatures => {
    // Super admin has all features enabled
    if (isSuperAdmin) {
      return {
        dashboard: true,
        trades: true,
        clients: true,
        riskMonitor: true,
        payouts: true,
        analytics: true,
      };
    }

    if (!featureFlags || !user) return defaultRoleFeatures;

    // Get user roles (support multi-role)
    const userRoles = user.roles || (user.role ? [user.role] : []);

    // Map user role to toggleable role
    const roleMap: Record<string, ToggleableRole | null> = {
      [UserRole.ADMIN]: 'admin',
      [UserRole.HR]: 'hr',
      [UserRole.EMPLOYEE]: 'employee',
      [UserRole.AGENT]: 'admin', // Agents use admin features
      [UserRole.CLIENT]: 'admin', // Clients use admin features (legacy)
    };

    // Find the first matching toggleable role from user's roles
    let toggleableRole: ToggleableRole | null = null;
    for (const role of userRoles) {
      if (roleMap[role]) {
        toggleableRole = roleMap[role];
        break;
      }
    }

    if (!toggleableRole) return defaultRoleFeatures;

    return featureFlags[toggleableRole] || defaultRoleFeatures;
  }, [featureFlags, user, isSuperAdmin]);

  // Check if a specific feature is enabled for current user
  const isFeatureEnabled = useCallback((feature: FeatureName): boolean => {
    return currentUserFeatures[feature] ?? false;
  }, [currentUserFeatures]);

  const value = useMemo<FeatureFlagsContextType>(() => ({
    featureFlags,
    isLoading,
    error,
    refetchFlags,
    toggleAllFeatures,
    toggleRoleFeatures,
    toggleFeature,
    isFeatureEnabled,
    getFeaturesForRole,
    currentUserFeatures,
  }), [
    featureFlags,
    isLoading,
    error,
    refetchFlags,
    toggleAllFeatures,
    toggleRoleFeatures,
    toggleFeature,
    isFeatureEnabled,
    getFeaturesForRole,
    currentUserFeatures,
  ]);

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

// ============================================================================
// USE FEATURE FLAGS HOOK
// ============================================================================

export function useFeatureFlags(): FeatureFlagsContextType {
  const context = useContext(FeatureFlagsContext);
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
}

export default FeatureFlagsContext;
